import { ComponentProps, KeyboardEvent, useCallback, useRef } from 'react';

import { Button } from '../../Button';
import { Input } from '../Input';

interface SearchInputProps extends Omit<ComponentProps<typeof Input>, 'onSubmit'> {
  readonly onSubmit?: (query: string) => void;
  readonly withButton?: boolean;
  readonly button?: Omit<ComponentProps<typeof Button>, 'onClick'>;
}

const defaultButtonProps: ComponentProps<typeof Button> = {
  children: 'Search',
};

function getInputValue(input: HTMLInputElement | null) {
  return input?.value ?? '';
}

function SearchInput({ withButton, onSubmit, onKeyUp, reverse = true, button = defaultButtonProps, ...props }: SearchInputProps) {
  const input = useRef<HTMLInputElement>(null);

  const onKeyUpHandler = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        onSubmit?.(e.currentTarget.value);
      }

      onKeyUp?.(e);
    },
    [onSubmit, onKeyUp],
  );

  const onSubmitHandler = useCallback(() => {
    onSubmit?.(getInputValue(input.current));
  }, [onSubmit]);

  return withButton ? (
    <Input.Group>
      <Input role="search" ref={input} icon="search" reverse={reverse} onKeyUp={onKeyUpHandler} {...props} />
      <Button onClick={onSubmitHandler} {...button} />
    </Input.Group>
  ) : (
    <Input role="search" ref={input} icon="search" reverse={reverse} onKeyUp={onKeyUpHandler} {...props} />
  );
}

export { SearchInput, getInputValue as _getInputValue };
